import { Modal, Col, Row } from "antd";
import { Flex, Box, Text } from "@chakra-ui/react";
import OutlineButton from "components/Button/OutlineButton";
import PrimaryButton from "components/Button/PrimaryButton";
import { useForm } from "react-hook-form";
import SelectContaCategoria from "ui/components/Input/SelectContaCategoria";
import Field from "components/Input/Field/index";
import InputMoney from "components/Input/InputMoney/index";
import ShadowSelectableButton from "components/Button/ShadowSelectableButton";
import { AiFillPlusCircle, AiOutlineMinusCircle } from "react-icons/ai";
import colors from "core/resources/theme/colors";
import { PrevisaoOrcamentariaLancamento } from "core/features/previsao-orcamentaria/typings/index";
import { showSuccess } from "infra/helpers/alerta";
import { useEffect } from "react";
import SelectMes from "components/Input/SelectMes";
import { OperacaoEnum } from "resources/enums/conta";
import SelectFormaDistribuicaoProps from "components/Input/SelectFormaDistriuicao/index";
import moment from "moment";
import { PlanejamentoOrcamentarioDetalheItemResponse } from "core/features/previsao-orcamentaria/typings";
import useAtualizarLancamento from "features/previsao-orcamentaria/hooks/useAtualizarLancamento";
import useCriarLancamento from "core/features/previsao-orcamentaria/hooks/useCriarLancamento";
import { useLoading } from "ui/components/GlobalLoading";

interface ModalLancamentoPrevisaoOrcamentariaProps {
  onClose: () => void;
  visible: boolean;
  previsaoId?: string;
  dataInicioPrevisao: string | undefined;
  data?: PlanejamentoOrcamentarioDetalheItemResponse | undefined;
  refetch: any;
}

const defaultValuesForm: Partial<PrevisaoOrcamentariaLancamento> = {
  valor: undefined,
  categoriaFinanceiraId: undefined,
  formaDistribuicao: undefined,
  operacao: OperacaoEnum.Despesa,
  mesInicio: undefined,
  mesFim: undefined,
};

export default function ModalLancamentoPrevisaoOrcamentaria({
  onClose,
  visible,
  previsaoId,
  dataInicioPrevisao,
  refetch,
  data,
}: ModalLancamentoPrevisaoOrcamentariaProps) {
  const { mutate: saveLancamento, isLoading: isAddLoading } =
    useCriarLancamento(previsaoId!);
  const { mutate: atualizarLancamento, isLoading: isAttLoading } =
    useAtualizarLancamento(previsaoId!);
  const { control, handleSubmit, reset, watch, setValue } =
    useForm<PrevisaoOrcamentariaLancamento>();

  const form = watch();
  const ano = moment(dataInicioPrevisao).format("YYYY");

  const onSubmit = async (form: PrevisaoOrcamentariaLancamento) => {
    if (data) {
      atualizarLancamento(form, {
        onSuccess: () => {
          onClose();
          showSuccess().then(refetch);
        },
      });
    } else {
      saveLancamento(form, {
        onSuccess: () => {
          onClose();
          showSuccess().then(refetch);
        },
      });
    }
  };

  useEffect(() => {
    if (data) {
      reset({
        categoriaFinanceiraId: data.id,
        valor: data.previsto,
        mesInicio: `${ano}-01`,
        mesFim: `${ano}-12`,
      });
    }
  }, [ano, data, reset]);

  useEffect(() => {
    if (!visible) {
      reset(defaultValuesForm);
    }
  }, [reset, visible]);

  useLoading(isAddLoading || isAttLoading);

  return (
    <Modal
      centered
      destroyOnClose
      width="500px"
      visible={visible}
      onCancel={onClose}
      title={data ? "Editar lançamento do período" : "Adicionar lançamento"}
      footer={
        <Flex flex={1} justify="end" gap={4}>
          <OutlineButton onClick={onClose}>Fechar</OutlineButton>
          <PrimaryButton onClick={() => handleSubmit(onSubmit)()}>
            Salvar
          </PrimaryButton>
        </Flex>
      }
    >
      <form onSubmit={(ev) => handleSubmit(onSubmit)(ev)}>
        {!data ? (
          <>
            <Field
              name="operacao"
              inputProps={{ type: "text" }}
              control={control}
              render={({ field }) => (
                <Box as={Row} gutter={16} mb={4}>
                  <Box as={Col} md={12}>
                    <ShadowSelectableButton
                      onClick={() => {
                        field.onChange(OperacaoEnum.Despesa);
                        setValue("categoriaFinanceiraId", undefined);
                      }}
                      selectedColor={colors.primary}
                      colorText={colors.black}
                      selected={form.operacao === OperacaoEnum.Despesa}
                    >
                      <Flex flexDir="column" align="center">
                        <Flex
                          w="35px"
                          h="35px"
                          align="center"
                          justify="center"
                          rounded="full"
                          bg={`${colors.primaryBlue}26`}
                        >
                          <AiOutlineMinusCircle
                            size={20}
                            color={colors.primary}
                          />
                        </Flex>

                        <Text>1. Despesa</Text>
                      </Flex>
                    </ShadowSelectableButton>
                  </Box>
                  <Box as={Col} md={12}>
                    <ShadowSelectableButton
                      onClick={() => {
                        field.onChange(OperacaoEnum.Receita);
                        setValue("categoriaFinanceiraId", undefined);
                      }}
                      selectedColor={colors.primary}
                      colorText={colors.black}
                      selected={form.operacao === OperacaoEnum.Receita}
                    >
                      <Flex flexDir="column" align="center">
                        <Flex
                          w="35px"
                          h="35px"
                          align="center"
                          justify="center"
                          rounded="full"
                          bg={`${colors.primaryBlue}26`}
                        >
                          <AiFillPlusCircle size={20} color={colors.primary} />
                        </Flex>
                        <Text>2. Receita</Text>
                      </Flex>
                    </ShadowSelectableButton>
                  </Box>
                </Box>
              )}
            />

            <Box as={Col} sm={24} mb={4}>
              <Field
                label="Categoria"
                control={control}
                name="categoriaFinanceiraId"
                render={({ field }) => (
                  <SelectContaCategoria
                    disableNotClassifiableNodes
                    key={form.operacao}
                    operacao={form.operacao}
                    {...field}
                  />
                )}
              />
            </Box>
          </>
        ) : null}

        <Box as={Col} sm={24} mb={4}>
          <Field
            label="Valor previsto"
            control={control}
            name="valor"
            rules={{
              required: "Campo obrigatório",
            }}
            render={({ field, isInvalid }) => (
              <InputMoney
                isInvalid={isInvalid}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </Box>

        <Box as={Row} gutter={32} mb={4}>
          <Box as={Col} sm={12}>
            <Field
              label="Mês de Inicio"
              control={control}
              name="mesInicio"
              rules={{
                required: "Campo obrigatório",
              }}
              render={({ field }) => <SelectMes ano={ano} {...field} />}
            />
          </Box>
          <Box as={Col} sm={12}>
            <Field
              label="Mês de Fim"
              control={control}
              name="mesFim"
              rules={{
                required: "Campo obrigatório",
              }}
              render={({ field }) => <SelectMes ano={ano} {...field} />}
            />
          </Box>
        </Box>
        <Box as={Col} sm={24} mb={4}>
          <Field
            label="Forma de distribuição"
            control={control}
            name="formaDistribuicao"
            rules={{
              required: "Campo obrigatório",
            }}
            render={({ field }) => <SelectFormaDistribuicaoProps {...field} />}
          />
        </Box>
      </form>
    </Modal>
  );
}
